import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useCeilingSpeakers} from "./rules/useCeilingSpeakers";
import {AudioConfig} from "../config/AudioConfig";
import {useFrontSpeakers} from "./rules/useFrontSpeakers";
import {useFrontSpeakerValidator} from "./rules/useFrontSpeakerValidator";
import {useCeilingSpeakerValidator} from "./rules/useCeilingSpeakerValidator";
import {useSideSpeakers} from "./rules/useSideSpeakers";
import {useSideSpeakerValidator} from "./rules/useSideSpeakerValidator";
import {useRearSpeakers} from "./rules/useRearSpeakers";
import {useRearSpeakerValidator} from "./rules/useRearSpeakerValidator";
import {useSubwoofers} from "./rules/useSubwoofers";
import {useCenterSpeaker} from "./rules/useCenterSpeaker";
import {useScreenConfig} from "./ScreenConfigContext";
import {SpeakerType, useRoomConfig} from "./RoomConfigContext";
import {useUserConfigContext} from "./UserConfigContext";

export const AudioConfigContext = createContext({});

export default function AudioConfigProvider({children}) {
    // Allows changing the speaker brands from the menu (currently only 1 speaker config exists):


    let defaultSurroundType = "7.1.4";
    let defaultRearInCeiling = false;
    let defaultConfig = AudioConfig.klipsch;

    const { userConfig } = useUserConfigContext();

    if (userConfig) {
        defaultSurroundType = userConfig.surroundType;
        defaultRearInCeiling = userConfig.rearInCeiling;

        if (userConfig.audioConfigId === "waterfall") {
            defaultConfig = AudioConfig.waterfall;
        }
    }

    const [config, setConfig] = useState(defaultConfig);
    const setConfigID = useCallback(id => setConfig(AudioConfig[id]), []);

    // provide values 5.1, 7.1, 5.1.2, 5.1.4, 7.1.2 or 7.1.4, 7.2.4, etc.
    const [surroundType, setSurroundType] = useState(defaultSurroundType);
    // whether or not the rear speakers should be in the ceiling
    const [rearInCeiling, setRearInCeiling] = useState(defaultRearInCeiling);
    // whether or not the audio range cones should be displayed
    const [showCones, setShowCones] = useState(false);
    const [builtInWall, setBuiltInWall] = useState(false);

    // splits the surroundType string into the number of speakers per type
    const [numRegularSpeakers, numWoofers, numCeilingSpeakers] = useMemo(() => {
        const split = surroundType.split(".")
        // always has woofer
        return [parseInt(split[0]), parseInt(split[1]), split.length > 2 ? parseInt(split[2]) : 0];
    }, [surroundType]);

    // rear in ceiling can only be true for N.1.2 systems, so force it to false if it changes to N.1.4
    useEffect(() => {
        if (numCeilingSpeakers !== 2)
            setRearInCeiling(false);
    }, [numCeilingSpeakers]);

    const { type: screenType, setAcousticTransparent } = useScreenConfig();

    // Atmos-based rules
    const frontPositions = useFrontSpeakers(config.front, builtInWall, config.type);
    const ceilingPositions = useCeilingSpeakers(numCeilingSpeakers, config.ceiling, frontPositions, builtInWall);
    const sidePositions = useSideSpeakers(numRegularSpeakers, config.surround, builtInWall);
    const rearPositions = useRearSpeakers(numRegularSpeakers, rearInCeiling, config.surround, config.ceiling, builtInWall);
    const centerPosition = useCenterSpeaker(config.center, builtInWall, config.type);
    const subwooferPositions = useSubwoofers(numWoofers, config.subwoofer, screenType, frontPositions, config.type);

    const { score: frontSpeakersScore, errorCodes: frontSpeakersErrorCodes} = useFrontSpeakerValidator(config.front, frontPositions, builtInWall);
    const { score: ceilingSpeakersScore, errorCodes: ceilingSpeakersErrorCodes} = useCeilingSpeakerValidator(config.ceiling, ceilingPositions);
    const { score: sideSpeakersScore, errorCodes: sideSpeakersErrorCodes} = useSideSpeakerValidator(numRegularSpeakers, config.surround, sidePositions);
    const { score: rearSpeakersScore, errorCodes: rearSpeakersErrorCodes} = useRearSpeakerValidator(numRegularSpeakers, rearInCeiling, config.surround, config.ceiling, rearPositions);

    const { setFrontMargin, setCeilingMargin, setSideMargin, setBackMargin } = useRoomConfig();

    useEffect(() => {
        if (config.type !== SpeakerType.Normal)
            // force this to be true
            setBuiltInWall(true);

    }, [config]);

    useEffect(() => {
        if (config.type !== SpeakerType.Waterfall && numWoofers > 1) {
            let val = numRegularSpeakers + "." + 1;

            if (numCeilingSpeakers > 0)
                val += "." + numCeilingSpeakers;

            setSurroundType(val);
        }
    }, [config, numCeilingSpeakers, numRegularSpeakers, numWoofers])

    useEffect(() => {
        // force acoustic transparent for waterfall
        if (config.type !== SpeakerType.Waterfall)
            setBuiltInWall(false);
    }, [config, setBuiltInWall])

    useEffect(() => {
        // force acoustic transparent for waterfall
        if (config.type === SpeakerType.Waterfall)
            setAcousticTransparent(true);
    }, [config, setAcousticTransparent])

    useEffect(() => {
        if (builtInWall || config.type !== SpeakerType.Normal) {
            setFrontMargin(Math.max(config.front.wallDepth, config.subwoofer.wallDepth, config.center.wallDepth));
            setCeilingMargin(config.ceiling.wallDepth);
            setSideMargin(config.surround.wallDepth);
            setBackMargin(config.surround.wallDepth);
        }
        else {
            setFrontMargin(0);
            setCeilingMargin(0);
            setSideMargin(0);
            setBackMargin(0);
        }
    }, [builtInWall, config, setFrontMargin, setCeilingMargin, setSideMargin, setBackMargin]);

    return (
        <AudioConfigContext.Provider value={{
            surroundType, setSurroundType,
            numRegularSpeakers, numWoofers, numCeilingSpeakers,
            rearInCeiling, setRearInCeiling,
            ceilingPositions, frontPositions, sidePositions, rearPositions,
            ...config, setConfigID,
            ceilingSpeakersScore, ceilingSpeakersErrorCodes,
            frontSpeakersScore, frontSpeakersErrorCodes,
            sideSpeakersScore, sideSpeakersErrorCodes,
            rearSpeakersScore, rearSpeakersErrorCodes,
            subwooferPositions, centerPosition,
            showCones, setShowCones,
            builtInWall, setBuiltInWall
        }}>
            {children}
        </AudioConfigContext.Provider>
    )
}

export const useAudioConfig = () => useContext(AudioConfigContext);
